/**
 * Firebase configuration for Identity Platform authentication
 *
 * INVITE-ONLY MODE:
 * - Users can ONLY be added via the invite script (scripts/invite_user.py)
 * - Self-registration is disabled in Identity Platform console
 * - Users receive an invitation email with password reset link
 *
 * Supported sign-in methods for invited users:
 * - Email/Password Sign-In (set via password reset link)
 * - Google Sign-In (OAuth) - gives us profile photo!
 */

import { initializeApp, FirebaseApp } from 'firebase/app';
import {
  getAuth,
  Auth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  verifyPasswordResetCode,
  confirmPasswordReset,
  User,
  UserCredential,
} from 'firebase/auth';

// Firebase configuration
// These values come from Google Cloud Identity Platform console
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'voice-chat-demo-479522.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'voice-chat-demo-479522',
};

// Initialize Firebase
let app: FirebaseApp;
let auth: Auth;

try {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  console.log('Firebase initialized for project:', firebaseConfig.projectId);
} catch (error) {
  console.error('Failed to initialize Firebase:', error);
  throw error;
}

// Auth providers
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account', // Always show account selector
});

/**
 * Sign in with Google
 */
export async function signInWithGoogle(): Promise<User> {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    console.log('Signed in with Google:', result.user.email);
    return result.user;
  } catch (error: unknown) {
    const authError = error as { code?: string; message?: string };
    console.error('Google sign-in error:', authError.code, authError.message);
    throw error;
  }
}

/**
 * Sign in with email and password
 */
export async function signInWithEmail(email: string, password: string): Promise<User> {
  try {
    const result: UserCredential = await signInWithEmailAndPassword(auth, email, password);
    console.log('Signed in with email:', result.user.email);
    return result.user;
  } catch (error: unknown) {
    const authError = error as { code?: string; message?: string };
    console.error('Email sign-in error:', authError.code, authError.message);
    throw error;
  }
}

/**
 * Create a new account with email and password
 */
export async function createAccount(email: string, password: string, displayName?: string): Promise<User> {
  try {
    const result: UserCredential = await createUserWithEmailAndPassword(auth, email, password);

    // Update display name if provided
    if (displayName && result.user) {
      await updateProfile(result.user, { displayName });
    }

    console.log('Account created for:', result.user.email);
    return result.user;
  } catch (error: unknown) {
    const authError = error as { code?: string; message?: string };
    console.error('Account creation error:', authError.code, authError.message);
    throw error;
  }
}

/**
 * Send password reset email via our backend (SendGrid)
 */
export async function resetPassword(email: string): Promise<void> {
  try {
    const response = await fetch('/api/forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (data.error) {
      throw new Error(data.error);
    }

    console.log('Password reset email sent via SendGrid to:', email);
  } catch (error) {
    console.error('Password reset error:', error);
    throw error;
  }
}

/**
 * Sign out
 */
export async function signOut(): Promise<void> {
  try {
    await firebaseSignOut(auth);
    console.log('Signed out');
  } catch (error) {
    console.error('Sign-out error:', error);
    throw error;
  }
}

/**
 * Get current ID token for API requests.
 * If token refresh fails, signs out the user.
 */
export async function getIdToken(forceRefresh: boolean = false): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) {
    return null;
  }
  try {
    return await user.getIdToken(forceRefresh);
  } catch (error: unknown) {
    const authError = error as { code?: string; message?: string };
    console.error('Failed to get ID token:', authError.code, authError.message);

    // If token refresh fails, sign out the user
    if (authError.code === 'auth/user-token-expired' ||
        authError.code === 'auth/invalid-user-token' ||
        authError.code === 'auth/user-disabled' ||
        authError.code === 'auth/network-request-failed') {
      console.warn('Token refresh failed, signing out user');
      try {
        await firebaseSignOut(auth);
      } catch (signOutError) {
        console.error('Failed to sign out after token error:', signOutError);
      }
    }
    return null;
  }
}

/**
 * Subscribe to auth state changes
 */
export function onAuthStateChange(callback: (user: User | null) => void): () => void {
  return onAuthStateChanged(auth, callback);
}

/**
 * Get user-friendly error message from Firebase auth error code
 */
export function getAuthErrorMessage(errorCode: string): string {
  switch (errorCode) {
    case 'auth/email-already-in-use':
      return 'Er bestaat al een account met dit e-mailadres.';
    case 'auth/invalid-email':
      return 'Voer een geldig e-mailadres in.';
    case 'auth/operation-not-allowed':
      return 'E-mail/wachtwoord accounts zijn niet ingeschakeld.';
    case 'auth/weak-password':
      return 'Wachtwoord moet minimaal 6 tekens zijn.';
    case 'auth/user-disabled':
      return 'Dit account is uitgeschakeld.';
    case 'auth/user-not-found':
      return 'Geen account gevonden met dit e-mailadres.';
    case 'auth/wrong-password':
      return 'Onjuist wachtwoord.';
    case 'auth/invalid-credential':
      return 'Ongeldige e-mail of wachtwoord.';
    case 'auth/too-many-requests':
      return 'Te veel pogingen. Probeer het later opnieuw.';
    case 'auth/network-request-failed':
      return 'Netwerkfout. Controleer je verbinding.';
    case 'auth/popup-closed-by-user':
      return 'Inloggen geannuleerd.';
    default:
      return 'Er is een fout opgetreden. Probeer het opnieuw.';
  }
}

/**
 * Verify a password reset code and return the associated email
 */
export async function verifyResetCode(oobCode: string): Promise<string> {
  return verifyPasswordResetCode(auth, oobCode);
}

/**
 * Complete password reset with a new password
 */
export async function completePasswordReset(oobCode: string, newPassword: string): Promise<void> {
  return confirmPasswordReset(auth, oobCode, newPassword);
}

export { auth };
export type { User };
